/**
 * RequireRole — Garde d'accès par rôle (RBAC frontend, défense en profondeur).
 *
 * L'enforcement de sécurité reste côté backend (403 par endpoint) ; cette garde
 * masque/bloque côté UI les surfaces réservées (ex. Users, Licences, Gouvernance)
 * pour les rôles non autorisés.
 *
 * Usage :
 *   <RequireRole allow={['admin']}><UsersPage /></RequireRole>
 */
import React from 'react';
import { ShieldX } from 'lucide-react';
import { useAuth, type AppRole } from '../../context/AuthContext.tsx';

export default function RequireRole({
  allow,
  children,
}: {
  allow: readonly AppRole[];
  children: React.ReactNode;
}): React.ReactElement {
  const { role, roleLoading } = useAuth();

  if (roleLoading) {
    return (
      <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
        Vérification des droits…
      </div>
    );
  }

  if (!role || !allow.includes(role)) {
    return (
      <div className="flex h-full min-h-[320px] flex-col items-center justify-center gap-4 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
          <ShieldX size={28} className="text-muted-foreground" />
        </div>
        <div>
          <p className="text-lg font-semibold text-foreground">Accès réservé</p>
          <p className="mt-1 max-w-xs text-sm text-muted-foreground">
            Cette section est réservée aux administrateurs. Votre rôle actuel
            {role ? ` (« ${role} »)` : ''} ne permet pas d'y accéder.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
